﻿import React, { useEffect, useState } from 'react';
import { GetMarketingItems, GetCostList, GetMarketsServedList } from 'api/marketing';
import { StrikeThroughHeader } from 'components/Headers/strikethrough-header/strikethrough-header';
import { useQuery } from '@tanstack/react-query';
import LoadingIndicator from 'components/Global/loading-indicator/loading-indicator';
import { XMasonry, XBlock } from 'react-xmasonry';
import { Card } from 'Components/Cards/card/card';
import { Link } from 'react-router-dom';
import Select from '@mui/material/Select';
import Input from '@mui/material/Input';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import AccordionDetails from '@mui/material/AccordionDetails';
import RenderIcon from '../../Software/Subpages/SoftwareList/RenderIcon';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
const SelectMemo = React.memo(Select);
const MenuItemMemo = React.memo(MenuItem);

export default function MarketingList() {
  const [marketList, setMarketingList] = useState(null);
  const [costList, setCostList] = useState([]);
  const [marketsServedList, setMarketsServedList] = useState([]);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [selectedCostFilter, setSelectedCost] = useState('ALL');

  const query = useQuery({ queryKey: ['GetMarketingItems'], queryFn: () => GetMarketingItems()() });
  const costQuery = useQuery({ queryKey: ['GetCostList'], queryFn: () => GetCostList()() });
  const msQuery = useQuery({
    queryKey: ['GetMarketsServedList'],
    queryFn: () => GetMarketsServedList()(),
  });

  const handleCostChange = (e) => {
    setSelectedCost(e.target.value);
  };

  const handleMarketChange = (e) => {
    setSelectedMarkets(e.target.value);
  };

  useEffect(() => {
    if (query.data != null) {
      setMarketingList(query.data);
    }
  }, [query.data]);

  useEffect(() => {
    if (costQuery.data) setCostList(costQuery.data);
  }, [costQuery.data]);

  useEffect(() => {
    if (msQuery.data) setMarketsServedList(msQuery.data);
  }, [msQuery.data]);

  if (query.isLoading) {
    return (
      <div id="marketing-root">
        <StrikeThroughHeader>Marketing</StrikeThroughHeader>
        <LoadingIndicator key="loading" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <>
        <StrikeThroughHeader>Marketing</StrikeThroughHeader>
        <div>Error. Please try again later.</div>
      </>
    );
  }
  if (query.isSuccess && query.data) {
    return (
      <div id="marketing-root">
        <StrikeThroughHeader>Marketing</StrikeThroughHeader>

        {/*Banner Image and Description*/}
        <div style={{ textAlign: 'center' }}>
          <img
            src={'/CmsImages/GettyImages-1200927810 CROPPED.jpg'}
            style={{ objectFit: 'cover', width: '100%', height: 'auto' }}
          />
        </div>
        <p style={{ textAlign: 'left', margin: '1em 0 1em 0' }}>
          People don't just buy a product—they buy a relationship. The relationship sets a company
          apart from its competition. How can we capture more relationships than our competition? By
          BRANDING! Branding is the process of differentiating yourself from your competition and
          bonding with your customers to create loyalty. It helps you establish a position in your
          market that your competition can't penetrate.
        </p>
        <p style={{ textAlign: 'left', margin: '1em 0 1em 0' }}>
          Start by thinking like your customers. You know they will not buy from you simply because
          of Baltimore Life's history of success. Your customer sees you – the agent – as the brand.
          Your customer wants to buy from an agent with a quality label—the Rolls Royce of agents.
        </p>
        <p style={{ textAlign: 'left', margin: '1em 0 1em 0' }}>
          The success of any brand goes well beyond any clever signage or logo. It includes the
          entire relationship formed with its customers. The purpose of this online Marketing
          section is to help you brand yourself in your market. Make yourself the first name that
          comes to mind when your customers think about life insurance!
        </p>

        <Filters
          selectedCostFilter={selectedCostFilter}
          selectedMarkets={selectedMarkets}
          costList={costList}
          marketsServed={marketsServedList}
          handleCostCallback={handleCostChange}
          handleMarketChangeCallback={handleMarketChange}
        />
        {marketList && (
          <MarketListView
            data={marketList}
            selectedCostFilter={selectedCostFilter}
            selectedMarkets={selectedMarkets}
          />
        )}
      </div>
    );
  }
}

function Filters({
  selectedCostFilter,
  selectedMarkets,
  costList,
  marketsServed,
  handleCostCallback,
  handleMarketChangeCallback,
}) {
  return (
    <div>
      <FormControl style={{ margin: '8px', minWidth: 120 }}>
        <InputLabel htmlFor="costFilter">Cost</InputLabel>
        <Select
          native
          value={selectedCostFilter}
          onChange={handleCostCallback}
          inputProps={{
            name: 'cost',
            id: 'costFilter',
          }}
        >
          <option aria-label="ALL" value="ALL">
            ALL
          </option>
          {costList &&
            costList.map((cl) => (
              <option key={cl.name} value={cl.name}>
                {cl.name}
              </option>
            ))}
        </Select>
      </FormControl>
      <FormControl style={{ margin: '8px', minWidth: 120 }}>
        <InputLabel htmlFor="marketFilter">Markets Served</InputLabel>
        <SelectMemo
          labelId="marketFilter"
          id="category-select-checkbox"
          multiple
          style={{ width: '200px' }}
          value={selectedMarkets}
          onChange={handleMarketChangeCallback}
          input={<Input />}
          renderValue={(selected) => selected.join(',')}
        >
          {marketsServed.map((market, i) => (
            <MenuItemMemo key={`${market.name}-${i}`} value={market.name}>
              <Checkbox checked={selectedMarkets.indexOf(market.name) > -1} />
              <ListItemText primary={market.name} />
            </MenuItemMemo>
          ))}
        </SelectMemo>
      </FormControl>
    </div>
  );
}

function MarketListView({ data, selectedCostFilter, selectedMarkets }) {
  const checkArray = (a1, a2) => {
    let rtn = 0;

    a1?.split(',').map((e, i) => {
      if (a2.includes(e)) {
        rtn++;
      }
    });

    return rtn > 0;
  };

  const DisplayRows = (itm) => {
    let filteredItems = [];
    switch (true) {
      case selectedCostFilter != 'ALL' && selectedMarkets.length == 0:
        filteredItems = itm.items.filter((f) => f.costGroup == selectedCostFilter);
        break;
      case selectedCostFilter == 'ALL' && selectedMarkets.length == 0:
        filteredItems = itm.items;
        break;
      case selectedCostFilter != 'ALL' && selectedMarkets.length > 0:
        filteredItems = itm.items.filter(
          (f) => f.costGroup == selectedCostFilter && checkArray(f.marketsServed, selectedMarkets)
        );
        break;
      case selectedCostFilter == 'ALL' && selectedMarkets.length > 0:
        filteredItems = itm.items.filter((f) => checkArray(f.marketsServed, selectedMarkets));
        break;
    }

    if (!filteredItems || filteredItems.length == 0) return <>None Found</>;
    else
      return filteredItems.map((itm) => (
        <li key={itm.itemName}>
          <Link to={{ pathname: '/Marketing/' + itm.itemName }}>
            <RenderIcon>
              <LaunchOutlinedIcon />
            </RenderIcon>
            {itm.itemName}
          </Link>
        </li>
      ));
  };

  return (
    <>
      <XMasonry maxColumns={3} responsive targetBlockWidth={500}>
        {data.campaigns.map((campaign, index) => (
          <XBlock key={campaign.campaignName}>
            <div className="m-2 software-list-wrapper">
              <Accordion defaultExpanded sx={{ backgroundColor: '#f2f2f2' }}>
                <AccordionSummary
                  id={`panel-${index}`}
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel3-content"
                  sx={{ backgroundColor: '#0075BF' }}
                >
                  <Typography sx={{ color: '#fff' }}>{campaign.campaignName}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ul>{DisplayRows(campaign)}</ul>
                </AccordionDetails>
              </Accordion>
            </div>
          </XBlock>
        ))}
      </XMasonry>
    </>
  );
}
